/**
 * Comparison Chart Types
 * Type definitions for country/region comparison charts
 * These charts display stacked bar comparisons with countries as series
 */

/**
 * Comparison chart data structure
 * Similar to V2ChartData but optimized for country comparisons
 */
export interface ComparisonChartData {
  chart_type: 'stacked_bar';
  title: string;
  subtitle: string;
  x_axis: string[];
  series: {
    name: string; // Country/region name
    color: string;
    data: number[];
    deal_counts?: number[]; // Optional deal counts for funding charts
    stack: string; // Stack identifier for ECharts (e.g., 'total')
  }[];
  totals: Record<string, number>; // Country name -> total amount/count
  summary: {
    grand_total: number;
    countries: Record<string, number>; // Country name -> total
    period_label: string;
  };
}

/**
 * Comparison response metadata
 * Extends base metadata with comparison-specific fields
 */
export interface ComparisonMetadata {
  timeframe: string;
  week_offset: number;
  current_period: {
    start: string;
    end: string;
  };
  navigation: {
    can_navigate_back: boolean;
    can_navigate_forward: boolean;
    current_offset: number;
    next_offset: number;
    prev_offset: number;
  };
  generated_at: string;
  total_records: number;
  years: number[];
  data_staleness?: {
    is_stale: boolean;
    is_very_stale: boolean;
    warning?: string;
    latest_active_date?: string;
    latest_date?: string;
    days_since_active?: number;
    days_since_latest?: number;
  };
  comparison_countries: string[]; // Array of country/region names being compared
}

/**
 * Full comparison response from API
 * Contains both funding trends and M&A activity charts
 */
export interface ComparisonResponse {
  comparison_type: 'us-israel' | 'us-europe' | 'us-uk' | 'us-eu' | 'us-canada' | 'us-japan' | 'us-australia';
  charts: {
    funding_trends: ComparisonChartData;
    ma_activity: ComparisonChartData;
  };
  metadata: ComparisonMetadata;
  cache_status?: 'HIT' | 'MISS' | 'FILTERED';
  response_time_ms?: number;
  filters_applied?: Record<string, string[]> | null;
}

/**
 * Valid comparison type identifiers
 */
export type ComparisonType = 'us-israel' | 'us-europe' | 'us-uk' | 'us-eu' | 'us-canada' | 'us-japan' | 'us-australia';

/**
 * Comparison configuration for UI components
 */
export interface ComparisonConfig {
  type: ComparisonType;
  label: string; // Display label (e.g., "US vs Israel")
  countries: string[]; // Array of countries being compared
  description?: string; // Optional description for UI
}

/**
 * Available comparison configurations
 */
export const COMPARISON_CONFIGS: Record<ComparisonType, ComparisonConfig> = {
  'us-israel': {
    type: 'us-israel',
    label: 'US vs Israel',
    countries: ['United States', 'Israel'],
    description: 'Compare US and Israeli cybersecurity markets'
  },
  'us-europe': {
    type: 'us-europe',
    label: 'US vs Europe',
    countries: ['United States', 'Europe'],
    description: 'Compare US and European cybersecurity markets'
  },
  'us-uk': {
    type: 'us-uk',
    label: 'US vs UK',
    countries: ['United States', 'United Kingdom'],
    description: 'Compare US and UK cybersecurity markets'
  },
  'us-eu': {
    type: 'us-eu',
    label: 'US vs EU',
    countries: ['United States', 'EU'],
    description: 'Compare US and EU cybersecurity markets'
  },
  'us-canada': {
    type: 'us-canada',
    label: 'US vs Canada',
    countries: ['United States', 'Canada'],
    description: 'Compare US and Canadian cybersecurity markets'
  },
  'us-japan': {
    type: 'us-japan',
    label: 'US vs Japan',
    countries: ['United States', 'Japan'],
    description: 'Compare US and Japanese cybersecurity markets'
  },
  'us-australia': {
    type: 'us-australia',
    label: 'US vs Australia',
    countries: ['United States', 'Australia'],
    description: 'Compare US and Australian cybersecurity markets'
  }
};
